import React from "react";
import * as THREE from "three";
import { ModelingMeasureRepresentation, ModelingType } from "@/services/context/viewer/modeling-tools/modeling-tools-context-provider.tsx";
import { BaamerFileRevisionDto } from "@/models/interfaces/dtos/baamer-file-revision-dto.ts";
import MeasureType from "./models/measure-type.ts";
import ProjectMeasures from "./models/project-measures.ts";
import MeasureShape from "./models/measure-shape.ts";
import Measure from "./models/measure.ts";
import FileRecord from "./models/file-record.ts";
import FragmentRecord from "./models/fragment-record.ts";

interface ProjectMeasureContextProps {
    projectMeasures: ProjectMeasures;

    baamerFileRevisions: BaamerFileRevisionDto[];
    storeysFileData: Map<string, Array<FileRecord>>;
    storeysFragsData: Map<string, Array<FragmentRecord>>;
    buildingsFileData: Map<string, Array<FileRecord>>;
    projectMeasureTypes: MeasureType[];
    predefinedShapes: MeasureShape[];

    // Liste temporaire des types de métré édités, envoyée à l'API uniquement lors d'une sauvegarde globale.
    tempMeasureTypes: MeasureType[];

    // Liste temporaire des IDs de types de métré à supprimer, envoyée à l'API uniquement lors d'une sauvegarde globale.
    deletedMeasureTypeIds: string[];

    isLoading: boolean;

    invalidateMeasureTypes: () => Promise<void>;

    addMeasureType: (measureRepresentationId: string, name: string, dataInformations: string) => Promise<MeasureType | null>;

    getMeasureType: (measureTypeId: string) => MeasureType | undefined;

    // Applique l'édition d'un type de métré en local (cache + liste temporaire) sans appeler l'API.
    editMeasureTypeLocally: (measureType: MeasureType) => void;

    // Envoie la liste temporaire des types de métré à l'API. Retourne true si l'envoi a réussi (ou s'il n'y avait rien à envoyer).
    saveMeasureTypes: () => Promise<boolean>;

    // Marque un type de métré pour suppression (local uniquement, pas d'appel API immédiat).
    markMeasureTypeForDeletion: (measureTypeId: string) => void;

    // Envoie les suppressions de types de métré à l'API. Retourne true si l'envoi a réussi (ou s'il n'y avait rien à envoyer).
    deleteMeasureTypes: () => Promise<boolean>;

    // Envoi les suppressions de métrés à partir de types de métrés indiqués à supprimer que pour l'étage indiqué
    deleteMeasuresFromMeasureTypesAndStorey: (storeyId: string) => Promise<boolean>;

    updateProjectMeasuresFromMeasuresTypes: (excludeStoreyIds: string[], measureTypeIds: string[]) => Promise<boolean>;

    updateMeasuresAndRecords: (buildingId: string, storeyId: string, measures: Measure[], fileRecords: FileRecord[], fragData: FragmentRecord[] | undefined) => void;

    getDrawingTypeFromMeasureTypeId: (measureTypeId: string) => ModelingType;
    getDrawingTypeFromShapeId: (shapeId: string) => ModelingType;

    getDrawingColorFromMeasureTypeId: (measureTypeId: string) => THREE.Color;

    getDrawingAlphaFromMeasureTypeId: (measureTypeId: string) => number;

    getDrawingMeasureRepresentation: (measureType: MeasureType) => ModelingMeasureRepresentation;
    getDrawingMeasureRepresentationFromMeasureTypeId: (measureTypeId: string) => ModelingMeasureRepresentation;

    getDrawingDataInformationsFromMeasureTypeId: (measureTypeId: string) => Record<string, unknown> | undefined;

    completeRefreshData: () => Promise<void>;
    refreshData: () => Promise<void>;
    
    clearTempData: () => void;
}

export default ProjectMeasureContextProps;

export const ProjectMeasureContext = React.createContext<ProjectMeasureContextProps | undefined>(undefined); 